import Cookies from "js-cookie";
import { login, logout } from "../../api/auth/authApi";
import { fetchMe, type MeResponse } from "../../api/healthcare/MeApi";
import { announceLogout, announceSignIn, endSession } from "../../lib/http/refresh";
import { isEnrolmentConfined, isSignedIn, setAuthenticated, type LoginAuthenticated, type LoginMfaRequired, type MfaStatus } from "../../lib/http/session";
import { clearSessionUser, setSessionUser } from "./sessionUser";
import { clearSubscriptions, setSubscriptions } from "../subscriptions/subscriptionService";
import { parsePreferences } from "../userPrefs/parsePreferences";
import type { User } from "../../types/admin/UserTypes";

const applyPreferences = (userPreferences: unknown): void => {
	const parsedPrefs = parsePreferences(userPreferences);
	if (!parsedPrefs) return;

	Cookies.set("userPreferences", JSON.stringify(parsedPrefs), { expires: 365 });

	// Apply theme immediately. Kept dynamic so theme code stays out of the public bundles.
	import("../userPrefs/themeUtils").then(({ applyThemeClasses }) => {
		applyThemeClasses((parsedPrefs.theme as "light" | "dark") || "light");
	});
};

// The profile and the clinic's plans live in memory only; plans first, so no guard ever sees a profile without them.
export const applySessionProfile = ({ user, subscriptions }: MeResponse): void => {
	setSubscriptions(subscriptions);
	setSessionUser(user);
};

export const establishSession = (me: MeResponse): void => {
	applySessionProfile(me);
	applyPreferences(me.user.preferences);
};

// enrolment_required: a recovery login or an expired grace; the session is confined and /me would answer 403 (contract §4.7).
export type SignInResult =
	| { status: "authenticated"; user: User; mfa: MfaStatus }
	| { status: "enrolment_required"; mfa: MfaStatus }
	| { status: "mfa_required"; challenge: LoginMfaRequired };

// identity has already set the HttpOnly cookies; the session store and the /me profile complete the sign-in.
export const completeSignIn = async (body: LoginAuthenticated): Promise<User> => {
	// /me before publishing: a session without its profile makes the root gate unmount the login page mid sign-in.
	const me = await fetchMe();
	setAuthenticated(body);
	announceSignIn();
	establishSession(me);
	return me.user;
};

// Every sign-in path ends here: password, the second factor, a passkey and recovery.
export const finishSignIn = async (body: LoginAuthenticated): Promise<SignInResult> => {
	if (isEnrolmentConfined(body)) {
		setAuthenticated(body);
		announceSignIn();
		return { status: "enrolment_required", mfa: body.mfa };
	}
	return { status: "authenticated", user: await completeSignIn(body), mfa: body.mfa };
};

export const signIn = async (username: string, password: string): Promise<SignInResult> => {
	const body = await login(username, password);
	if (body.status === "mfa_required") return { status: "mfa_required", challenge: body };
	return finishSignIn(body);
};

let inFlight: Promise<User> | null = null;

// Single-flight: the root gate and a route guard can both ask on the same render.
export const bootstrapSession = (): Promise<User> => {
	if (!inFlight) {
		inFlight = fetchMe()
			.then((me) => {
				// A sign-out while /me was in flight must not write the identity back.
				if (isSignedIn()) applySessionProfile(me);
				return me.user;
			})
			.finally(() => {
				inFlight = null;
			});
	}
	return inFlight;
};

// Everything this tab holds about the signed-in user; the credential itself is HttpOnly and only identity clears it.
export const clearSession = (): void => {
	clearSubscriptions();
	Cookies.remove("userPreferences");
	clearSessionUser();
};

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

// Always ends the local session, even when identity cannot be reached; the other tabs follow over the channel.
export const signOut = async (): Promise<void> => {
	try {
		await logout();
	} catch (error) {
		console.error("session.logout.failed", { status: statusOf(error) });
	} finally {
		announceLogout();
		endSession("logged_out", { broadcast: false });
		clearSession();
	}
};
